import { UpdateNutritionalInfo } from '@/src/types/apps/gygIngredientsNutritionalInfo'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_CREATE,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_DELETE,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_GENERATE_MENU,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_LIST,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_RESTORE,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_S3_GET_FILE,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_SEARCH,
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_UPDATE
} from './action'

interface IngredientsNutritionalInfo {
  nutrtionalInfoData: UpdateNutritionalInfo[]
  searchData?: UpdateNutritionalInfo[]
  success?: string | null
  error?: string | null
  loading?: boolean
  deleted?: boolean
  restore?: boolean
  meta?: {
    totalRows: number
    pages: number
  }
  s3FileData?: string | null
}

const initialState: IngredientsNutritionalInfo = {
  nutrtionalInfoData: [],
  searchData: [],
  success: null,
  error: null,
  loading: false,
  deleted: false,
  restore: false,
  meta: {
    totalRows: 0,
    pages: 0
  },
  s3FileData: null
}

interface IngredientsNutritionalInfoResponse {
  data: UpdateNutritionalInfo[]
  meta?: {
    totalRows: number
    pages: number
  }
}

export const getIngredientsNutritionalInfo = createAsyncThunk<IngredientsNutritionalInfoResponse, void, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_LIST,
  async (_, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient('ingredients-nutritional-info')
      const data = responseData.data as IngredientsNutritionalInfoResponse

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch ingredients nutritional info')
    }
  }
)

export const createIngredientsNutritionalInfo = createAsyncThunk<UpdateNutritionalInfo, any, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_CREATE,
  async (nutritionalInfoData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('ingredients-nutritional-info', nutritionalInfoData)
      const data = response.data

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to create nutritional info')
    }
  }
)

export const updateIngredientsNutritionalInfo = createAsyncThunk<UpdateNutritionalInfo, UpdateNutritionalInfo, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_UPDATE,
  async (nutritionalInfoData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(
        `ingredients-nutritional-info/${nutritionalInfoData.id}`,
        nutritionalInfoData
      )
      const data = response.data

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to update nutritional info')
    }
  }
)

export const deleteIngredientsNutritionalInfo = createAsyncThunk<string, string, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_DELETE,
  async (id, { extra, rejectWithValue }) => {
    try {
      await extra.httpClient.delete(`ingredients-nutritional-info/${id}`)

      return id
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to delete nutritional info')
    }
  }
)

export const restoreIngredientsNutritionalInfo = createAsyncThunk<UpdateNutritionalInfo, string, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_RESTORE,
  async (id, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(`ingredients-nutritional-info/${id}/restore`)
      const data = response.data

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to restore nutritional info')
    }
  }
)

export const searchIngredientsNutritionalInfoByString = createAsyncThunk<UpdateNutritionalInfo[], any, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_SEARCH,
  async (searchString, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(`ingredients-nutritional-info/info/byString`, searchString)
      const data = response.data as UpdateNutritionalInfo[]

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to search nutritional info')
    }
  }
)

export const generateMenuNutritionalInfo = createAsyncThunk<void, void, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_GENERATE_MENU,
  async (_, { extra, rejectWithValue }) => {
    try {
      await extra.httpClient.get(`ingredients-nutritional-info/info/nutritional-values`)
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to generate menu')
    }
  }
)

export const getFileFromS3Bucket = createAsyncThunk<string, string, Redux>(
  RESTO365_INGREDIENTS_NUTRITIONAL_INFO_S3_GET_FILE,
  async (fileName, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get(`/ingredients-nutritional-info/s3/getFilefromAWS?key=${fileName}`)

      return response.data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch file from S3')
    }
  }
)

export const gygIngredientsNutritionalInfo = createSlice({
  name: RESTO365_INGREDIENTS_NUTRITIONAL_INFO,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      /**
       * Fetch Ingredients nutritional Info List
       */
      .addCase(getIngredientsNutritionalInfo.fulfilled, (state, action) => {
        state.nutrtionalInfoData = action.payload.data
        state.deleted = false
        state.restore = false
        state.success = null
        state.loading = false
        state.error = null
      })
      .addCase(getIngredientsNutritionalInfo.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch ingredients nutritional info'
        state.success = null
      })
      .addCase(getIngredientsNutritionalInfo.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Create Ingredients Nutritional Info
       */
      .addCase(createIngredientsNutritionalInfo.fulfilled, state => {
        state.success = 'Nutritional info created successfully'
        state.loading = false
        state.deleted = false
        state.restore = false
        state.error = null
      })
      .addCase(createIngredientsNutritionalInfo.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to create nutritional info'
        state.success = null
      })
      .addCase(createIngredientsNutritionalInfo.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Update Ingredients Nutritional Info
       */
      .addCase(updateIngredientsNutritionalInfo.fulfilled, state => {
        state.success = 'Nutritional info updated successfully'
        state.loading = false
        state.deleted = false
        state.restore = false
        state.error = null
      })
      .addCase(updateIngredientsNutritionalInfo.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to update nutritional info'
        state.success = null
      })
      .addCase(updateIngredientsNutritionalInfo.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Delete Ingredients Nutritional Info
       */
      .addCase(deleteIngredientsNutritionalInfo.fulfilled, (state, action) => {
        state.nutrtionalInfoData = state.nutrtionalInfoData.map(item =>
          String(item.id) === String(action.payload) ? { ...item, isDeleted: true } : item
        )
        state.success = 'Nutritional info deleted successfully'
        state.deleted = true
        state.loading = false
        state.error = null
      })
      .addCase(deleteIngredientsNutritionalInfo.rejected, (state, action) => {
        state.deleted = false
        state.loading = false
        state.error = (action.payload as string) || 'Failed to delete nutritional info'
        state.success = null
      })
      .addCase(deleteIngredientsNutritionalInfo.pending, state => {
        state.deleted = false
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Restore Ingredients Nutritional Info
       */
      .addCase(restoreIngredientsNutritionalInfo.fulfilled, (state, action) => {
        state.nutrtionalInfoData = state.nutrtionalInfoData.map(item =>
          String(item.id) === String(action.payload) ? { ...item, isDeleted: false } : item
        )
        state.success = 'Nutritional info restored successfully'
        state.restore = true
        state.loading = false
        state.error = null
      })
      .addCase(restoreIngredientsNutritionalInfo.rejected, (state, action) => {
        state.restore = false
        state.loading = false
        state.error = (action.payload as string) || 'Failed to restore nutritional info'
        state.success = null
      })
      .addCase(restoreIngredientsNutritionalInfo.pending, state => {
        state.restore = false
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Search Ingredients Nutritional Info by String
       */
      .addCase(searchIngredientsNutritionalInfoByString.fulfilled, (state, action) => {
        state.searchData = action.payload
        state.success = null
        state.loading = false
        state.error = null
      })
      .addCase(searchIngredientsNutritionalInfoByString.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to search nutritional info'
        state.success = null
      })
      .addCase(searchIngredientsNutritionalInfoByString.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Generate Menu Nutritional Info
       */
      .addCase(generateMenuNutritionalInfo.fulfilled, state => {
        state.success = 'Menu nutritional info generated successfully'
        state.loading = false
        state.deleted = false
        state.restore = false
        state.error = null
      })
      .addCase(generateMenuNutritionalInfo.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to generate menu'
        state.success = null
      })
      .addCase(generateMenuNutritionalInfo.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Get File From S3 Bucket
       */
      .addCase(getFileFromS3Bucket.fulfilled, (state, action) => {
        state.s3FileData = action.payload
        state.success = 'File fetched from S3 successfully'
        state.loading = false
        state.error = null
      })
      .addCase(getFileFromS3Bucket.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to fetch file from S3'
        state.success = null
      })
      .addCase(getFileFromS3Bucket.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
  }
})

export default gygIngredientsNutritionalInfo.reducer
